import React, { useCallback } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import MaterialList from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Link from '@material-ui/core/Link';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Avatar from 'components/Avatar';
import Typography from '@material-ui/core/Typography';
import Tooltip from 'components/Tooltip';
import { ReactComponent as LanguageIcon } from 'assets/svg/language.svg';
import { Grid } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    paddingTop: 0,
  },
  isSelected: {
    backgroundColor: '#EDFBFF',
  },
  emptyRows: {
    height: 66,
  },
  inline: {
    lineHeight: 'normal',
    fontWeight: 'bold',
  },
  pl2: {
    paddingLeft: theme.spacing(2),
  },
  pr: {
    paddingRight: 100,
  },
  visited: {
    '&:visited': {
      color: theme.palette.primary.main,
    },
  },
  supportedLanguagesWrapper: {
    paddingLeft: theme.spacing(0.7),
  },
  supportedLanguages: {
    color: theme.palette.primary.main,
    width: 26,
    height: 26,
  },
  primaryHeading: {
    flexWrap: 'nowrap',
  },
}));

export default function List(props) {
  const classes = useStyles();
  const theme = useTheme();
  const {
    trialId,
    loading = false,
    error = false,
    loadingText = 'loading...',
    unit = 'miles',
    noResultMessage = 'No results found',
    data = [],
    accessor = {},
    onListItemClick = () => {},
    countPerPage = 10,
    currentPage = 1,
    activeId,
    buttonLabel = 'Prescreen',
    noAvatar = false,
    onSiteConnectClick = null,
    siteLanguageSingleText = '',
    siteLanguageMultipleText = '',
  } = props;
  const emptyRows = data.length < countPerPage ? countPerPage - data.length : 0;

  const formatSiteLanguages = useCallback(
    (site) => {
      const languages = site.languages;

      if (Array.isArray(languages) && languages.length) {
        if (languages.length === 1) {
          return siteLanguageSingleText.replace('[[LANGUAGE]]', languages[0]);
        } else {
          const prefixLanguages = languages.slice(0, -1).join(', ');
          const suffixLanguages = languages.slice(-1);

          let result = siteLanguageMultipleText;

          result = result
            .replace('[[PREFIX_LANGUAGE]]', prefixLanguages)
            .replace('[[SUFFIX_LANGUAGE]]', suffixLanguages);

          return result;
        }
      }
    },
    [siteLanguageSingleText, siteLanguageMultipleText],
  );

  return (
    <MaterialList className={classes.root}>
      {loading ? (
        <div>{loadingText}</div>
      ) : (
        data.map((item, index) => {
          const isActive = item.site_id === activeId;
          return (
            <ListItem
              className={isActive ? classes.isSelected : ''}
              classes={{ secondaryAction: classes.pr }}
              style={{
                alignItems: 'center',
                height:
                  '-webkit-fit-content' /* Fix safari 13+ item height issue */,
              }}
              onClick={() => onListItemClick(item[accessor.id], item)}
              key={index}
              button
              disableGutters
            >
              {!noAvatar && (
                <Avatar
                  // alt="abc" src="/static/images/avatar/2.jpg"
                  variant="square"
                  size="flex"
                  isList
                  avatarStyle={{
                    color: 'inherit',
                  }}
                >
                  {index + 1 + countPerPage * (currentPage - 1)}
                </Avatar>
              )}
              <ListItemText
                // classes={{ root: noAvatar ? '' : classes.pl2 }}
                primary={
                  <Grid
                    container
                    alignItems="baseline"
                    classes={{ root: classes.primaryHeading }}
                  >
                    <Grid item zeroMinWidth>
                      <Typography
                        component="p"
                        variant="body1"
                        className={classes.inline}
                        title={item[accessor.primary]}
                        noWrap
                        gutterBottom
                      >
                        {item[accessor.primary]}
                      </Typography>
                    </Grid>
                    {Array.isArray(item.languages) && item.languages.length ? (
                      <Grid
                        item
                        pl={3}
                        classes={{ root: classes.supportedLanguagesWrapper }}
                      >
                        <Tooltip
                          placement="top"
                          title={formatSiteLanguages(item)}
                        >
                          <span>
                            <LanguageIcon
                              className={classes.supportedLanguages}
                            />
                          </span>
                        </Tooltip>
                      </Grid>
                    ) : null}
                  </Grid>
                }
                secondary={
                  <Typography
                    component="p"
                    color="textSecondary"
                    variant="caption"
                    title={item[accessor.secondary]}
                    noWrap
                  >
                    {item[accessor.secondary]}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                {item[accessor.actionText] && (
                  <Typography
                    component="p"
                    color="textSecondary"
                    variant="caption"
                    align="right"
                  >
                    {item[accessor.actionText] + ' ' + unit}
                  </Typography>
                )}
                <Link
                  href={onSiteConnectClick ? '#' : item[accessor.link]}
                  className={classes.visited}
                  data-site-id={item[accessor.id]}
                  onClick={
                    onSiteConnectClick
                      ? (e) =>
                          onSiteConnectClick(
                            { trialId, site: item, distanceUnit: unit },
                            e,
                          )
                      : null
                  }
                >
                  <Typography variant="body2" component="div" align="right">
                    {buttonLabel}
                  </Typography>
                </Link>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })
      )}
      {error ? <Alert severity="error">{error}</Alert> : ''}
      {!loading && !error && data.length === 0 ? (
        <Alert severity="info">{noResultMessage}</Alert>
      ) : (
        ''
      )}
      {!loading && !error && emptyRows
        ? Array(emptyRows)
            .fill()
            .map(() => <div className={classes.emptyRows}></div>)
        : ''}

      {/* <Divider variant="inset" component="li" /> */}
    </MaterialList>
  );
}
